/* Recording state per room (§11). The authoritative per-user view (session,
   my consent, the pending roster) comes from the capture API; the sync loop's
   app.atrium.capture.* state events are the live trigger to re-fetch it — one
   debounced refresh per burst, so a flurry of consent events costs one call.

   The store also narrates transitions as toasts: request appeared, recording
   started, stopped, request declined/lapsed. */
import { defineStore } from 'pinia'
import { sync } from '@/lib/sync'
import { matrix } from '@/lib/matrix'
import { captureApi, type CaptureRoomInfo, type CaptureSession } from '@/lib/captureApi'
import { useToasts } from '@/stores/toasts'

const CAPTURE_TYPES = ['app.atrium.capture.active', 'app.atrium.capture.consent', 'app.atrium.capture.policy']

interface RoomCapture {
  info: CaptureRoomInfo | null
  loaded: boolean
}

export const useCapture = defineStore('capture', {
  state: () => ({
    byRoom: {} as Record<string, RoomCapture>,
    wired: false,
    timers: {} as Record<string, number>,
    busy: false,
  }),

  getters: {
    infoOf: (s) => (roomId: string): CaptureRoomInfo | null => s.byRoom[roomId]?.info ?? null,
    session: (s) => (roomId: string): CaptureSession | null => s.byRoom[roomId]?.info?.session ?? null,
    recording(): (roomId: string) => boolean {
      return (roomId) => this.session(roomId)?.state === 'recording'
    },
    pendingConsent(): (roomId: string) => boolean {
      return (roomId) => this.session(roomId)?.state === 'pending_consent'
    },
    /** A live session I haven't accepted → the consent modal (and the pre-join
        gate: no call mount until this clears). */
    needsMyConsent(): (roomId: string) => boolean {
      return (roomId) => {
        const sess = this.session(roomId)
        if (!sess) return false
        return (sess.state === 'pending_consent' || sess.state === 'recording') && sess.my_consent !== 'accepted'
      }
    },
  },

  actions: {
    init() {
      if (this.wired) return
      this.wired = true
      sync.on('stateEv', (roomId, ev) => {
        if (!CAPTURE_TYPES.includes(ev.type)) return
        if (!this.byRoom[roomId]) return // not a room we're watching
        this.scheduleRefresh(roomId)
      })
    },

    /** Start watching a room (idempotent; called when a room view opens). */
    async ensure(roomId: string): Promise<void> {
      this.init()
      if (!roomId) return
      if (!this.byRoom[roomId]) this.byRoom[roomId] = { info: null, loaded: false }
      await this.refresh(roomId)
    },

    scheduleRefresh(roomId: string) {
      // One refresh per burst of state events (session start writes several).
      if (this.timers[roomId]) return
      this.timers[roomId] = window.setTimeout(() => {
        delete this.timers[roomId]
        void this.refresh(roomId)
      }, 250)
    },

    async refresh(roomId: string): Promise<void> {
      const slot = this.byRoom[roomId]
      if (!slot) return
      try {
        const info = await captureApi.info(roomId)
        this.narrate(roomId, slot.info?.session ?? null, info.session)
        slot.info = info
        slot.loaded = true
      } catch {
        /* capture service down / not a member — leave the last known state */
      }
    },

    /** Toast the transitions a user should notice without staring at the dock. */
    narrate(roomId: string, prev: CaptureSession | null, next: CaptureSession | null) {
      void roomId
      const toasts = useToasts()
      const me = matrix.userId()
      if (!prev && next?.state === 'pending_consent' && next.by !== me) {
        toasts.push({
          icon: 'radio', color: 'var(--danger, #e5484d)', title: 'Recording requested',
          text: 'Everyone in the call is being asked for consent.',
        })
      } else if (prev?.state === 'pending_consent' && next?.state === 'recording') {
        toasts.push({
          icon: 'radio', color: 'var(--danger, #e5484d)', title: 'Recording started',
          text: 'Everyone consented. A transcript and summary will follow.',
        })
      } else if (prev && !next) {
        if (prev.state === 'recording') {
          toasts.push({
            icon: 'check', color: 'var(--ok, #30a46c)', title: 'Recording stopped',
            text: 'Transcribing now — the summary lands in the room chat.',
          })
        } else if (prev.state === 'pending_consent') {
          toasts.push({
            icon: 'x', title: 'Recording request ended',
            text: 'Someone declined, the request lapsed, or it was cancelled.',
          })
        }
      }
    },

    /* ---- actions (each refreshes the room's picture) ---- */

    async start(roomId: string): Promise<string | null> {
      this.busy = true
      try {
        await captureApi.start(roomId)
        await this.refresh(roomId)
        return null
      } catch (e) {
        return e instanceof Error ? e.message : 'Could not start the recording'
      } finally {
        this.busy = false
      }
    },

    async consent(roomId: string, decision: 'accept' | 'decline'): Promise<string | null> {
      const sess = this.session(roomId)
      if (!sess) return 'No live recording session'
      this.busy = true
      try {
        await captureApi.consent(sess.id, decision)
        await this.refresh(roomId)
        return null
      } catch (e) {
        return e instanceof Error ? e.message : 'Could not record your decision'
      } finally {
        this.busy = false
      }
    },

    async stop(roomId: string): Promise<string | null> {
      this.busy = true
      try {
        await captureApi.stop(roomId)
        await this.refresh(roomId)
        return null
      } catch (e) {
        return e instanceof Error ? e.message : 'Could not stop the recording'
      } finally {
        this.busy = false
      }
    },

    async setPolicy(roomId: string, policy: 'host' | 'consensus' | 'forbidden'): Promise<string | null> {
      try {
        await captureApi.setPolicy(roomId, policy)
        await this.refresh(roomId)
        return null
      } catch (e) {
        return e instanceof Error ? e.message : 'Could not change the policy'
      }
    },
  },
})
